import { useState, useEffect, useRef } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { 
  Upload, 
  Download, 
  Trash2, 
  RefreshCw, 
  Package, 
  Smartphone,
  CheckCircle,
  XCircle,
  Clock,
  FileArchive,
  AlertCircle
} from "lucide-react";

const LiveUpdatesPage = () => {
  const [updates, setUpdates] = useState([]);
  const [stats, setStats] = useState({ total_updates: 0, total_downloads: 0 });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  
  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    version: "",
    platform: "all",
    channel: "production",
    message: "",
    major: false
  });

  // Admin key from env or default - matches backend default
  const ADMIN_KEY = process.env.REACT_APP_LIVE_UPDATE_KEY || "bizilgo-live-update-key-2024";

  useEffect(() => {
    fetchUpdates();
  }, []);

  const fetchUpdates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/updates/stats?admin_key=${ADMIN_KEY}`);
      setUpdates(res.data.updates || []);
      setStats({
        total_updates: res.data.total_updates || 0,
        total_downloads: res.data.total_downloads || 0
      });
    } catch (error) {
      console.error("Failed to fetch updates:", error);
      toast.error("Failed to load updates");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".zip")) {
      toast.error("Please select a .zip file");
      return;
    }

    if (!uploadForm.version) {
      toast.error("Please enter a version number");
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("app_id", "com.bizilgo.customer");
    formData.append("version", uploadForm.version);
    formData.append("platform", uploadForm.platform);
    formData.append("channel", uploadForm.channel);
    formData.append("message", uploadForm.message);
    formData.append("major", uploadForm.major.toString());
    formData.append("admin_key", ADMIN_KEY);

    try {
      const res = await axios.post(`${API}/updates/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });

      toast.success(`Update v${res.data.version} uploaded successfully!`);
      setUploadForm({ version: "", platform: "all", channel: "production", message: "", major: false });
      fileInputRef.current.value = "";
      fetchUpdates();
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (appId, version) => {
    if (!confirm(`Are you sure you want to delete version ${version}?`)) return;

    try {
      await axios.delete(`${API}/updates/version/${appId.replace(".", "_")}/${version}?admin_key=${ADMIN_KEY}`);
      toast.success(`Version ${version} deleted`);
      fetchUpdates();
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete update");
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                <Package className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Updates</p>
                <p className="text-2xl font-bold">{stats.total_updates}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
                <Download className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Downloads</p>
                <p className="text-2xl font-bold">{stats.total_downloads}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                <Smartphone className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Versions</p>
                <p className="text-2xl font-bold">{updates.filter(u => u.is_active).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upload New Update */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Upload New Update
          </CardTitle>
          <CardDescription>
            Upload a new OTA bundle for the Customer PWA app
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="version">Version *</Label>
              <Input
                id="version"
                placeholder="e.g., 1.0.15"
                value={uploadForm.version}
                onChange={(e) => setUploadForm({ ...uploadForm, version: e.target.value })}
                data-testid="update-version-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="platform">Platform</Label>
              <select
                id="platform"
                className="w-full h-10 px-3 rounded-md border border-input bg-background"
                value={uploadForm.platform}
                onChange={(e) => setUploadForm({ ...uploadForm, platform: e.target.value })}
                data-testid="update-platform-select"
              >
                <option value="all">All Platforms</option>
                <option value="android">Android Only</option>
                <option value="ios">iOS Only</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="channel">Channel</Label>
              <select
                id="channel"
                className="w-full h-10 px-3 rounded-md border border-input bg-background"
                value={uploadForm.channel}
                onChange={(e) => setUploadForm({ ...uploadForm, channel: e.target.value })}
                data-testid="update-channel-select"
              >
                <option value="production">Production</option>
                <option value="beta">Beta</option>
                <option value="dev">Development</option>
              </select>
            </div>

            <div className="space-y-2 flex items-center gap-4 pt-6">
              <Switch
                id="major"
                checked={uploadForm.major}
                onCheckedChange={(checked) => setUploadForm({ ...uploadForm, major: checked })}
                data-testid="update-major-switch"
              />
              <Label htmlFor="major" className="cursor-pointer">
                Major Update (requires restart)
              </Label>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="message">Changelog / Message</Label>
              <Textarea
                id="message"
                placeholder="What's new in this version..."
                value={uploadForm.message}
                onChange={(e) => setUploadForm({ ...uploadForm, message: e.target.value })}
                rows={3}
                data-testid="update-message-input"
              />
            </div>

            <div className="md:col-span-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".zip"
                className="hidden"
                onChange={handleUpload}
                data-testid="update-file-input"
              />
              
              <Button
                onClick={handleFileSelect}
                disabled={uploading || !uploadForm.version}
                className="w-full"
                data-testid="upload-bundle-btn"
              >
                {uploading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Uploading... {uploadProgress}%
                  </>
                ) : (
                  <>
                    <FileArchive className="w-4 h-4 mr-2" />
                    Select & Upload Bundle (.zip)
                  </>
                )}
              </Button>

              {uploading && (
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Instructions */}
          <div className="mt-4 p-4 bg-muted rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-muted-foreground mt-0.5" />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium mb-1">How to create a bundle:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Run <code className="bg-background px-1 rounded">yarn build</code> in the frontend directory</li>
                  <li>Navigate to the <code className="bg-background px-1 rounded">build</code> folder</li>
                  <li>Zip the contents: <code className="bg-background px-1 rounded">zip -r bundle.zip .</code></li>
                  <li>Upload the bundle.zip file here</li>
                </ol>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Updates List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Update History</CardTitle>
            <CardDescription>All uploaded OTA updates</CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchUpdates}
            disabled={loading}
            data-testid="refresh-updates-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : updates.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No updates uploaded yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {updates.map((update, idx) => (
                <div
                  key={idx}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg gap-4"
                  data-testid={`update-item-${update.version}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-full ${update.is_active ? "bg-green-100 dark:bg-green-900/30" : "bg-gray-100 dark:bg-gray-800"}`}>
                      {update.is_active ? (
                        <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                      ) : (
                        <XCircle className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">v{update.version}</span>
                        <Badge variant={update.is_active ? "default" : "secondary"}>
                          {update.is_active ? "Active" : "Inactive"}
                        </Badge>
                        <Badge variant="outline">{update.platform}</Badge>
                        <Badge variant="outline">{update.channel}</Badge>
                        {update.major && <Badge variant="destructive">Major</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {update.message || "No changelog"}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(update.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Download className="w-3 h-3" />
                          {update.downloads || 0} downloads
                        </span>
                        <span>{formatBytes(update.file_size)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 sm:flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(update.bundle_url, "_blank")}
                      data-testid={`download-${update.version}-btn`}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(update.app_id, update.version)}
                      data-testid={`delete-${update.version}-btn`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LiveUpdatesPage;
